import React, { forwardRef, Fragment, MouseEvent } from 'react';
import Svg from 'svg-inline-react';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { Link } from '@src/components/Link';
import { Size } from './enums/size.enum';
import { Appearance } from './enums/appearance.enum';
import { ButtonProps } from './props';
import './styles.scss';

export * from './enums/appearance.enum';
export * from './enums/size.enum';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      icon,
      appearance = Appearance.PRIMARY,
      isCircle = false,
      size = Size.MEDIUM,
      isDisabled = false,
      className,
      href,
      isLink,
      onClick,
      type,
      ...props
    },
    ref,
  ) => {
    const classes = classNames(
      'fr-button',
      `fr-button--${appearance}`,
      `fr-button--${size}`, // Adjust button size depend on padding
      { 'fr-button--circle': isCircle },
      { [`fr-button--circle-${size}`]: isCircle }, // Adjust circle button size depend on width and height
      { 'fr-button--icon': !!icon },
      { 'fr-button--only-icon': !children },
      { 'fr-button--disabled': isDisabled },
      { [`fr-button--disabled-${appearance}`]: isDisabled },
      className,
    );

    const content = (
      <Fragment>
        {icon && <Svg src={icon} />}
        <span>{children}</span>
      </Fragment>
    );

    const debouncedClick = debounce((event: MouseEvent<HTMLElement>) => {
      onClick?.(event);
    }, 200);

    if (href) {
      return (
        <a {...props} className={classes} disabled={isDisabled} href={href} onClick={debouncedClick} ref={ref}>
          {content}
        </a>
      );
    }

    if (isLink) {
      return (
        <Link className={classes} ref={ref} {...props} onClick={debouncedClick}>
          {content}
        </Link>
      );
    }

    return (
      <button {...props} className={classes} disabled={isDisabled} onClick={debouncedClick} ref={ref} type={type}>
        {content}
      </button>
    );
  },
);
